import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { calculateQuoteTotals } from '../utils/quoteTotals';
import { fetchWithReliability, DEVELLO_API_ORIGIN, toUserMessage } from '../utils/asyncReliability';
import { devClickDebug } from '../devClickDebug';
import { devLog } from '../utils/logger';

const QuoteFormModal = ({
  quoteForm,
  setQuoteForm,
  showCreateQuote,
  showEditQuote,
  setShowCreateQuote,
  setShowEditQuote,
  setShowReviewQuote,
  saveQuote,
  clients,
  materialSearch,
  setMaterialSearch,
  materialResults,
  setMaterialResults,
  materialQty,
  setMaterialQty,
  materialPrice,
  setMaterialPrice,
  materialSearchLoading,
  materialHistory,
  setShowCustomMaterialsList,
  debouncedSearch,
  saveMaterialToHistory,
  setShowCustomMaterialModal,
  materialsSubtotal,
  markupAmount,
  total,
  handleQuoteFormChange,
  showBreakdown,
  setShowBreakdown,
  locationCountry,
  setLocationCountry,
  locationRegion,
  setLocationRegion,
  locationCity,
  setLocationCity,
  locationRegions,
  ...rest
}) => {
  // hasSearched state removed - no longer needed
  const canSearchMaterials = rest.canSearchMaterials === true;
  const ACTION_GUARD_RESET_MS = 15000;
  const [isActionTransitioning, setIsActionTransitioning] = useState(false);
  const actionGuardTimerRef = useRef(null);

  const clearActionGuard = () => {
    setIsActionTransitioning(false);
    if (actionGuardTimerRef.current) {
      clearTimeout(actionGuardTimerRef.current);
      actionGuardTimerRef.current = null;
    }
  };
  
  const locationReady = !!locationCountry && !!locationRegion && locationCity.trim().length > 0;
  const buildSearchLocation = () => {
    const cityValue = locationCity.trim().replace(/\s+/g, ' ');
    if (!cityValue || !locationRegion || !locationCountry) return '';
    return locationCountry === 'US'
      ? `${cityValue}, ${locationRegion}, USA`
      : `${cityValue}, ${locationRegion}, Canada`;
  };

  // Add state for location validation
  const [locationValidation, setLocationValidation] = useState(null);
  const [locationValidationMessage, setLocationValidationMessage] = useState('');
  
  // Add state for searchable client dropdown
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);

  // Sync label when clientId changes — not when user clears/types to pick another client
  useEffect(() => {
    if (!quoteForm.clientId) return;
    const selectedClient = clients.find((c) => c.id === quoteForm.clientId);
    if (selectedClient) {
      setClientSearch(selectedClient.name);
    }
  }, [quoteForm.clientId, clients]);

  useEffect(() => {
    if (showCreateQuote || showEditQuote) return;
    clearActionGuard();
  }, [showCreateQuote, showEditQuote]);

  useEffect(
    () => () => {
      if (actionGuardTimerRef.current) clearTimeout(actionGuardTimerRef.current);
    },
    []
  );

  // Test location function
  const testLocation = async () => {
    if (!locationCity.trim()) {
      setLocationValidation('invalid');
      setLocationValidationMessage('Enter a city');
      return;
    }
    if (!locationRegion) {
      setLocationValidation('invalid');
      setLocationValidationMessage('Select a state/province');
      return;
    }

    setLocationValidation('testing');
    setLocationValidationMessage('Testing location...');

    try {
      const location = buildSearchLocation();
      const res = await fetchWithReliability(`${DEVELLO_API_ORIGIN}/api/test-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ location }),
        name: 'quoteForm:testLocation',
        timeoutMs: 35000,
        retries: 1,
      });
      if (!res.ok) {
        setLocationValidation('invalid');
        setLocationValidationMessage(res.userMessage);
        return;
      }
      const data = /** @type {{ valid?: boolean; error?: string }} */ (res.data);
      if (data.valid) {
        setLocationValidation('valid');
        setLocationValidationMessage('Valid location — ready for search.');
      } else {
        setLocationValidation('invalid');
        setLocationValidationMessage(
          data.error ? `Invalid location: ${data.error}` : 'Invalid location. Please check city/state/country.'
        );
      }
    } catch (error) {
      setLocationValidation('invalid');
      setLocationValidationMessage(toUserMessage(error, 'Could not verify location. Please try again.'));
    }
  };
  if (!(showCreateQuote || showEditQuote)) return null;

  // Use the same calculation as the PDF
  const totals = calculateQuoteTotals(quoteForm);

  // Fix: Only reset the entire form on explicit cancel/new actions
  const handleCancel = () => {
    clearActionGuard();
    setShowCreateQuote(false);
    setShowEditQuote(false);
    // Use the centralized reset function from Dashboard
    if (rest.resetQuoteForm) {
      rest.resetQuoteForm();
    } else {
      // Fallback reset if resetQuoteForm is not available
      setQuoteForm(prev => ({
        ...prev,
        clientId: '',
        materials: [],
        labour: { entries: [], total: 0 },
        taxEnabled: false,
        taxRate: '',
        description: '',
        editedOn: new Date().toISOString(),
        quoteNumber: '',
        // Preserve custom quote data when canceling
        customQuoteData: prev.customQuoteData || {}
      }));
      setMaterialSearch('');
      setMaterialResults([]);
      setMaterialQty(1);
      setMaterialPrice('');
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 sm:items-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-6xl shadow-2xl relative flex flex-col" style={{ height: '92vh', maxHeight: '92vh' }}>
        {/* Header */}
        <div className="px-4 py-3 border-b-2 bg-gray-50 sm:px-6 sm:py-4 shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Invoices</p>
              <h3 className="text-lg font-bold text-gray-900 sm:text-xl">{showCreateQuote ? 'Create New Invoice' : 'Edit Invoice'}</h3>
            </div>
            <button 
              onClick={() => {
                setShowCreateQuote(false);
                setShowEditQuote(false);
                if (rest.resetQuoteForm) {
                  rest.resetQuoteForm();
                } else {
                  setQuoteForm(prev => ({ 
                    ...prev,
                    clientId: '', 
                    materials: [], 
                    labour: { entries: [], total: 0 },
                    description: '', 
                    editedOn: new Date().toISOString(),
                    quoteNumber: '',
                    customQuoteData: prev.customQuoteData || {}
                  }));
                  setMaterialSearch('');
                  setMaterialResults([]);
                  setMaterialQty(1);
                  setMaterialPrice('');
                }
              }}
              className="rounded-xl p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        {/* Main Content: single-column scroll on mobile, side-by-side on lg */}
        <div className="min-h-0 flex-1 overflow-y-auto lg:overflow-hidden lg:flex lg:flex-row">
          {/* Left: Material Search */}
          <div className="w-full p-4 sm:p-6 lg:w-2/3 lg:min-h-0 lg:overflow-y-auto lg:flex-shrink-0">
            {/* Client Selection */}
            <div className="mb-4">
              <label className="block text-base font-semibold text-gray-700 mb-2">Client</label>
              <div className="relative">
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => {
                    const v = e.target.value;
                    setClientSearch(v);
                    setShowClientDropdown(true);
                    if (v === '') {
                      handleQuoteFormChange('clientId', '');
                    }
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  onBlur={() => {
                    // Delay hiding dropdown to allow clicking
                    setTimeout(() => setShowClientDropdown(false), 200);
                  }}
                  placeholder="Search for a client..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                {showClientDropdown && (
                  <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {clients
                      .filter(client => 
                        client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                        client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                      )
                      .map(client => (
                        <div
                          key={client.id}
                          onClick={() => {
                            handleQuoteFormChange('clientId', client.id);
                            setClientSearch(client.name);
                            setShowClientDropdown(false);
                          }}
                          className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">{client.name}</div>
                          {client.email && (
                            <div className="text-xs text-gray-500">{client.email}</div>
                          )}
                        </div>
                      ))}
                    {clients.filter(client => 
                      client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
                      client.email?.toLowerCase().includes(clientSearch.toLowerCase())
                    ).length === 0 && (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No clients found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            {/* Material Search/Add */}
            <div className="flex-1 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-base font-semibold text-gray-700">Search & Add Materials</label>
                {!materialSearch && materialHistory.length > 0 && (
                  <button
                    onClick={() => setShowCustomMaterialsList(true)}
                    className="flex items-center gap-1 bg-cyan-50 border border-cyan-200 rounded px-3 py-1 hover:bg-cyan-100 transition-colors text-sm"
                  >
                    <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span className="text-cyan-700 text-xs">Custom ({materialHistory.length})</span>
                  </button>
                )}
              </div>
              {/* Location Selector */}
              <div className="mb-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Search Location</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <input
                    type="text"
                    value={locationCity}
                    onChange={(e) => {
                      setLocationCity(e.target.value);
                      if (locationValidation !== 'testing') {
                        setLocationValidation(null);
                        setLocationValidationMessage('');
                      }
                    }}
                    placeholder="Enter city (e.g. Toronto or Dallas)"
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <select
                    value={locationRegion}
                    onChange={(e) => {
                      setLocationRegion(e.target.value);
                      setLocationValidation(null);
                      setLocationValidationMessage('');
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Select state/province</option>
                    {locationRegions.map((region) => (
                      <option key={region.code} value={region.code}>
                        {region.name} ({region.code})
                      </option>
                    ))}
                  </select>
                  <select
                    value={locationCountry}
                    onChange={(e) => {
                      setLocationCountry(e.target.value);
                      setLocationRegion('');
                      setLocationValidation(null);
                      setLocationValidationMessage('');
                    }}
                    className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="CA">Canada (CA)</option>
                    <option value="US">United States (US)</option>
                  </select>
                </div>
                <div className="mt-2 flex justify-end">
                  <button
                    onClick={testLocation}
                    disabled={locationValidation === 'testing' || !locationReady}
                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    title="Verify location coverage"
                  >
                    {locationValidation === 'testing' ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    ) : (
                      'Verify location'
                    )}
                  </button>
                </div>
                {locationValidation && (
                  <p className={`text-xs mt-1 ${
                    locationValidation === 'valid' ? 'text-green-600' :
                    locationValidation === 'invalid' ? 'text-red-600' :
                    'text-blue-600'
                  }`}>
                    {locationValidationMessage}
                  </p>
                )}
                {!locationCity.trim() && <p className="text-xs text-red-600 mt-0.5">Enter a city</p>}
                {!locationRegion && <p className="text-xs text-red-600 mt-0.5">Select a state/province</p>}
              </div>

              <div className="flex gap-2 mb-3">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={materialSearch}
                    onChange={e => {
                      const value = e.target.value;
                      setMaterialSearch(value);
                      // Clear results if search is empty
                      if (!value.trim()) {
                        setMaterialResults([]);
                      }
                    }}
                    onKeyPress={e => {
                      if (e.key === 'Enter' && canSearchMaterials && locationValidation === 'valid' && locationReady) {
                        // We need to call the search function from props
                        if (rest.searchMaterials) {
                          rest.searchMaterials(materialSearch);
                        }
                      }
                    }}
                    placeholder={
                      !canSearchMaterials
                        ? 'Material search is available on Pro only'
                        : (locationValidation === 'valid' && locationReady
                            ? 'Search for materials (e.g., lumber, concrete, nails)...'
                            : 'Please select a valid location first')
                    }
                    disabled={!canSearchMaterials || locationValidation !== 'valid' || !locationReady}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      canSearchMaterials && locationValidation === 'valid' && locationReady
                        ? 'border-gray-300' 
                        : 'border-blue-300 bg-blue-50 text-blue-900 placeholder-blue-400 cursor-not-allowed'
                    }`}
                  />
                  <button
                    onClick={() => {
                      if (!canSearchMaterials) return;
                      if (rest.searchMaterials) {
                        rest.searchMaterials(materialSearch);
                      }
                    }}
                    disabled={!canSearchMaterials || materialSearchLoading || !materialSearch.trim() || locationValidation !== 'valid' || !locationReady}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-primary disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
                    title={
                      !canSearchMaterials
                        ? 'Material search is available on Pro only'
                        : (locationValidation === 'valid' && locationReady ? 'Search materials' : 'Please select a valid location first')
                    }
                  >
                    {materialSearchLoading ? (
                      <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              

              {canSearchMaterials && materialSearchLoading && (
                <div className="border rounded-lg bg-gray-50 mb-3 p-6 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-2"></div>
                  <span className="text-sm text-gray-600">Searching for materials...</span>
                </div>
              )}

              
              {canSearchMaterials && !materialSearchLoading && materialResults.length > 0 && (
                <div className="border rounded-lg bg-white shadow-inner mb-3" style={{ height: '250px', overflowY: 'auto' }}>
                  <div className="grid grid-cols-1 gap-2 p-3">
                    {materialResults.map(mat => (
                      <div key={mat.id} 
                        className={`flex items-center gap-3 p-3 hover:bg-gray-50 border rounded-lg transition-colors ${
                          mat.isHistory ? 'bg-cyan-50 border-cyan-300 shadow-sm' : 'border-gray-200'
                        }`}
                      >
                        {/* Product Image */}
                        {mat.thumbnail && (
                          <div className="flex-shrink-0">
                            <img 
                              src={mat.thumbnail} 
                              alt={mat.name}
                              className="w-12 h-12 object-cover rounded-lg border"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}
                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <div className={`font-medium text-sm truncate ${
                            mat.isHistory ? 'text-cyan-900' : 'text-gray-900'
                          }`}>
                            {mat.link && !mat.isHistory ? (
                              <a 
                                href={mat.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="hover:text-primary transition-colors cursor-pointer flex items-center gap-1"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {mat.name}
                                <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              </a>
                            ) : (
                              mat.name
                            )}
                          </div>
                          {mat.source && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                mat.isHistory ? 'bg-cyan-200 text-cyan-800 border border-cyan-300' :
                                mat.source.toLowerCase().includes('home depot') ? 'bg-orange-100 text-orange-700' :
                                mat.source.toLowerCase().includes('rona') ? 'bg-blue-100 text-blue-700' :
                                mat.source.toLowerCase().includes('lowes') ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {mat.isHistory ? '📚 Your History' : mat.source}
                              </span>
                            </div>
                          )}
                        </div>
                        {/* Price and Actions */}
                        <div className="flex-shrink-0 text-right">
                          <div className={`text-base font-bold ${
                            mat.isHistory ? 'text-cyan-900' : 'text-gray-900'
                          }`}>${mat.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          <div className="text-xs text-gray-500">per unit</div>
                          {!mat.isHistory && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                saveMaterialToHistory({ name: mat.name, price: mat.price });
                              }}
                              className="mt-1 bg-cyan-500 text-white px-2 py-0.5 rounded text-xs hover:bg-cyan-600 transition-colors"
                            >
                              Save
                            </button>
                          )}
                        </div>
                        {/* Click to select */}
                        <div 
                          className="flex-shrink-0 cursor-pointer"
                          onClick={() => {
                            setMaterialSearch(mat.name);
                            setMaterialPrice(mat.price);
                            setMaterialResults([]);
                            if (rest.setHasSearched) {
                              rest.setHasSearched(false);
                            }
                          }}
                        >
                          <svg className={`w-5 h-5 hover:text-primary transition-colors ${
                            mat.isHistory ? 'text-cyan-600' : 'text-gray-400'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {canSearchMaterials && !materialSearchLoading && rest.hasSearched && materialResults.length === 0 && materialSearch.trim().length >= 2 && (
                <div className="border rounded-lg bg-gray-50 mb-3 p-6 text-center">
                  <div className="flex items-center justify-center text-gray-500">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.47-.881-6.08-2.33" />
                    </svg>
                    No materials found for "{materialSearch}"
                  </div>
                </div>
              )}
              {/* Add Material Form */}
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Material Name</label>
                    <input
                      type="text"
                      value={materialSearch}
                      onChange={e => setMaterialSearch(e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-transparent text-sm"
                      placeholder="Material name"
                    />
                  </div>
                  <div className="w-16 sm:w-20">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Qty</label>
                    <input
                      type="number"
                      min="1"
                      value={materialQty}
                      onChange={e => setMaterialQty(Number(e.target.value))}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div className="w-24 sm:w-28">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Unit Price</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={materialPrice}
                      onChange={e => setMaterialPrice(e.target.value)}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <button
                    onClick={() => {
                      if (!materialSearch || !materialPrice || materialQty < 1) return;
                      devLog('🔧 AGGRESSIVE FIX: Adding material:', { name: materialSearch, qty: materialQty, price: parseFloat(materialPrice) });
                      devLog('🔧 AGGRESSIVE FIX: Current materials:', quoteForm.materials);
                      setQuoteForm(prev => {
                        const newMaterials = [
                          ...(prev.materials || []),
                          { name: materialSearch, qty: materialQty, price: parseFloat(materialPrice) }
                        ];
                        devLog('🔧 AGGRESSIVE FIX: New materials after addition:', newMaterials);
                        return {
                          ...prev,
                          materials: newMaterials
                        };
                      });
                      setMaterialSearch('');
                      setMaterialPrice('');
                      setMaterialQty(1);
                      if (rest.setHasSearched) {
                        rest.setHasSearched(false);
                      }
                    }}
                    className="bg-primary text-white px-4 py-1.5 rounded hover:bg-primary-dark transition-colors font-medium text-sm"
                  >
                    Add
                  </button>
                </div>
                {/* Add Custom Material Button */}
                <div className="mt-3 pt-2 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomMaterialsList(false);
                      setShowCustomMaterialModal(true);
                    }}
                    className="flex items-center gap-1 text-cyan-600 hover:text-cyan-700 font-medium text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Add Custom Material
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Right: Materials List & Summary */}
          <div className="w-full border-t border-gray-200 lg:w-1/3 lg:border-t-0 lg:border-l lg:min-h-0 lg:overflow-y-auto lg:flex-1 lg:flex lg:flex-col">
            {/* Materials in Quote */}
            <div className="p-4 sm:p-6 lg:flex-1 lg:flex lg:flex-col lg:min-h-0">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-xl font-bold text-gray-800">Materials</h4>
                <span className="text-lg text-gray-500 font-medium">{quoteForm.materials.length} item(s)</span>
              </div>
              <div className="border rounded-lg bg-white shadow-inner mb-4 lg:flex-1 lg:overflow-y-auto lg:min-h-0" style={{ minHeight: '80px' }}>
                {quoteForm.materials && quoteForm.materials.length > 0 ? (
                  <div className="p-4 space-y-3">
                    {quoteForm.materials.map((mat, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 truncate">{mat.name}</div>
                          <div className="text-xs text-gray-500">Qty: {mat.qty} × ${mat.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <div className="font-semibold text-sm">${(mat.qty * mat.price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                          </div>
                          <button
                            className="text-red-500 hover:text-red-700 text-xs font-medium"
                            onClick={() => {
                              devLog('🔧 AGGRESSIVE FIX: Removing material at index:', idx);
                              devLog('🔧 AGGRESSIVE FIX: Current materials:', quoteForm.materials);
                              setQuoteForm(prev => {
                                const newMaterials = prev.materials.filter((_, i) => i !== idx);
                                devLog('🔧 AGGRESSIVE FIX: New materials after removal:', newMaterials);
                                devLog('🔧 AGGRESSIVE FIX: Updated quoteForm will have materials:', newMaterials);
                                return {
                                  ...prev,
                                  materials: newMaterials
                                };
                              });
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full text-gray-400 text-center p-8">
                    <div>
                      <div className="text-4xl mb-3">📋</div>
                      <div className="text-lg">No materials added yet</div>
                      <div className="text-sm mt-1">Search and add materials from the left panel</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            {/* Quote Summary */}
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <h4 className="text-base font-bold text-gray-800 mb-3">Summary</h4>
              <div className="space-y-2 text-sm">
                {showBreakdown && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Materials:</span>
                    <span className="font-semibold">${(totals.materialsSubtotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}
                {showBreakdown && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Labour:</span>
                    <span className="font-semibold">${(totals.labourTotal || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    {quoteForm.taxEnabled !== false ? 'Materials (including tax):' : 'Materials:'}
                  </span>
                  <span className="font-semibold">
                    $
                    {(quoteForm.taxEnabled !== false
                      ? totals.materialsWithTax
                      : totals.materialsSubtotal
                    ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
                {quoteForm.taxEnabled !== false && (
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    Tax (
                    {quoteForm.taxRate === '' || quoteForm.taxRate == null ? '0' : quoteForm.taxRate}
                    %):
                  </span>
                  <span className="font-semibold">${(totals.materialsTax || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
                )}
                <hr className="border-gray-300" />
                <div className="flex justify-between text-base">
                  <span className="font-bold text-gray-800">Total:</span>
                  <span className="font-bold text-primary">${(totals.materialsWithTax || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Footer: Actions */}
        <div className="shrink-0 px-4 pb-4 pt-3 bg-white rounded-b-2xl border-t-2 border-gray-200 shadow-lg sm:px-6">
          <div className="flex flex-wrap items-end gap-3 justify-between">
            <div className="flex flex-wrap items-end gap-3">
              <div className="w-36 sm:w-40">
                <label className="block text-sm font-semibold text-gray-700 mb-1">Invoice / doc #</label>
                <input
                  type="text"
                  value={quoteForm.quoteNumber}
                  onChange={e => setQuoteForm(prev => ({ ...prev, quoteNumber: e.target.value }))}
                  placeholder="e.g. INV-12"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 cursor-pointer shrink-0 pb-1">
                <input
                  type="checkbox"
                  checked={quoteForm.taxEnabled !== false}
                  onChange={e =>
                    setQuoteForm((prev) => ({
                      ...prev,
                      taxEnabled: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300"
                />
                Apply tax
              </label>
              {quoteForm.taxEnabled !== false && (
              <div className="w-24 sm:w-28">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Rate (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={quoteForm.taxRate === '' || quoteForm.taxRate == null ? '' : quoteForm.taxRate}
                  onChange={e => {
                    const value = e.target.value;
                    setQuoteForm(prev => ({ 
                      ...prev, 
                      taxRate: value === '' ? '' : parseFloat(value) || 0 
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  placeholder="e.g. 13"
                />
              </div>
              )}
            </div>
            <div className="flex gap-2 ml-auto">
            <button
              onClick={handleCancel}
              className="bg-gray-200 text-gray-800 px-5 py-2.5 rounded-lg hover:bg-gray-300 transition-colors text-sm font-semibold"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (isActionTransitioning) return;
                devClickDebug('QuoteFormModal: Review / Save Changes button click', {
                  hasClientId: !!quoteForm.clientId,
                  materialCount: quoteForm.materials?.length ?? 0,
                  showEditQuote,
                });
                if (!quoteForm.clientId) {
                  devClickDebug('QuoteFormModal: silent early return — no clientId', {});
                  return;
                }
                if (quoteForm.materials.length === 0) {
                  devClickDebug('QuoteFormModal: silent early return — no materials', {});
                  return;
                }
                
                // AGGRESSIVE FIX: Debug the materials being saved
                devLog('🔧 AGGRESSIVE FIX: Save Changes clicked');
                devLog('🔧 quoteForm.materials:', quoteForm.materials);
                devLog('🔧 quoteForm.materials.length:', quoteForm.materials.length);
                devLog('🔧 showEditQuote:', showEditQuote);

                setIsActionTransitioning(true);
                actionGuardTimerRef.current = setTimeout(() => {
                  devClickDebug('QuoteFormModal: action guard auto-reset fired', {});
                  clearActionGuard();
                }, ACTION_GUARD_RESET_MS);
                
                // AGGRESSIVE FIX: Always show review modal for consistency
                // This ensures the same workflow for both creating and editing
                devLog('🔧 AGGRESSIVE FIX: Showing review modal for both create and edit');
                setShowCreateQuote(false);
                setShowEditQuote(false);
                setShowReviewQuote(true);
                clearActionGuard();
              }}
              className={`px-5 py-2.5 rounded-lg transition-colors text-sm font-semibold ${
                !quoteForm.clientId || quoteForm.materials.length === 0 || isActionTransitioning
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-primary text-white hover:bg-primary-dark'
              }`}
              disabled={!quoteForm.clientId || quoteForm.materials.length === 0 || isActionTransitioning}
            >
              {isActionTransitioning ? 'Opening...' : showCreateQuote ? 'Review' : 'Save Changes'}
            </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuoteFormModal; 